import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/news-supabase-integration/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface Article {
  id?: string;
  title: string;
  description: string;
  url: string;
  image_url?: string;
  published_at: string;
  source_name: string;
  source_domain?: string;
  country: string;
  language: string;
  ai_summary?: string;
  created_at?: string;
  updated_at?: string;
}

interface FilterOptions {
  country?: string;
  dateFilter?: 'all' | 'today' | 'yesterday' | 'specific';
  specificDate?: Date;
  searchQuery?: string;
}

const dedupeArticlesByTitle = (items: Article[]) => {
  const seenTitles = new Set<string>();
  const uniqueArticles: Article[] = [];

  for (const article of items) {
    const normalizedTitle = article.title.trim().toLowerCase();

    if (seenTitles.has(normalizedTitle)) {
      continue;
    }

    seenTitles.add(normalizedTitle);
    uniqueArticles.push(article);
  }

  return uniqueArticles;
};

export const useArticles = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentFilters, setCurrentFilters] = useState<FilterOptions>({});
  const { toast } = useToast();

  const fetchArticles = async (reset: boolean = true, filters: FilterOptions = {}) => {
    setLoading(true);
    try {
      let query = supabase
        .from('articles')
        .select('*', { count: 'exact' });

      // Apply filters
      if (filters.country) {
        query = query.eq('country', filters.country);
      }

      if (filters.searchQuery?.trim()) {
        query = query.or(`title.ilike.%${filters.searchQuery}%,description.ilike.%${filters.searchQuery}%`);
      }

      if (filters.dateFilter && filters.dateFilter !== 'all') {
        const now = new Date();
        let startDate: Date;
        
        if (filters.dateFilter === 'today') {
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        } else if (filters.dateFilter === 'yesterday') {
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
        } else if (filters.dateFilter === 'specific' && filters.specificDate) {
          startDate = new Date(filters.specificDate.getFullYear(), filters.specificDate.getMonth(), filters.specificDate.getDate());
        } else {
          startDate = new Date(0); // Fallback
        }
        
        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 1);
        
        query = query
          .gte('published_at', startDate.toISOString())
          .lt('published_at', endDate.toISOString());
      }

      const offset = reset ? 0 : articles.length;
      query = query
        .order('created_at', { ascending: false })
        .range(offset, offset + 24);

      const { data, error, count } = await query;

      if (error) throw error;
      
      const newArticles = data || [];
      
      if (reset) {
        setArticles(dedupeArticlesByTitle(newArticles));
      } else {
        setArticles(prev => dedupeArticlesByTitle([...prev, ...newArticles]));
      }

      setHasMore(newArticles.length === 25 && (count || 0) > offset + 25);
      setCurrentFilters(filters);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'Failed to fetch articles',
        variant: 'destructive',
      });
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreArticles = () => {
    if (!loading && hasMore) {
      fetchArticles(false, currentFilters);
    }
  };

  const saveArticle = async (article: Omit<Article, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      // Check if article already exists
      const { data: existingArticle } = await supabase
        .from('articles')
        .select('id')
        .eq('url', article.url)
        .single();

      if (existingArticle) {
        return existingArticle;
      }

      const { data, error } = await supabase
        .from('articles')
        .insert([{
          title: article.title,
          description: article.description,
          url: article.url,
          image_url: article.image_url,
          published_at: article.published_at,
          source_name: article.source_name,
          source_domain: article.source_domain,
          country: article.country,
          language: article.language,
          ai_summary: article.ai_summary,
        }])
        .select()
        .single();

      if (error) throw error;
      
      return data;
    } catch (error: any) {
      console.error('Error saving article:', error);
      throw error;
    }
  };

  const updateArticleSummary = async (articleId: string, summary: string) => {
    try {
      const { data, error } = await supabase
        .from('articles')
        .update({ 
          ai_summary: summary,
          description: null // Remove description when summary is generated
        })
        .eq('id', articleId)
        .select();

      if (error) throw error;
      
      // Update local state
      setArticles(prev => 
        prev.map(article => 
          article.id === articleId 
            ? { ...article, ai_summary: summary, description: null }
            : article
        )
      );
      
      return data;
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'Failed to save summary',
        variant: 'destructive',
      });
      console.error('Error updating article summary:', error);
      throw error;
    }
  };

  const saveArticles = async (newArticles: Omit<Article, 'id' | 'created_at' | 'updated_at'>[]) => {
    try {
      const savedArticles = [];
      
      for (const article of newArticles) {
        try {
          const saved = await saveArticle(article);
          savedArticles.push(saved);
        } catch (error) {
          // Continue with other articles even if one fails
          console.error('Failed to save article:', article.title, error);
        }
      }

      if (savedArticles.length > 0) {
        toast({
          title: 'Success',
          description: `Saved ${savedArticles.length} articles`,
        });
        
        // Refresh articles list
        await fetchArticles();
      }
      
      return savedArticles;
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'Failed to save articles',
        variant: 'destructive',
      });
      console.error('Error saving articles:', error);
    }
  };

  useEffect(() => {
    fetchArticles();
  }, []);

  return {
    articles,
    loading,
    hasMore,
    currentFilters,
    fetchArticles,
    loadMoreArticles,
    saveArticle,
    saveArticles,
    updateArticleSummary,
  };
};